/**
 * Mobile Dashboard Component
 * Professional mobile-optimized dashboard for project management, quick mix, and analytics
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import NotificationCenter from '../components/NotificationCenter';
import { FACTORY_PRESETS, applyPresetToTrack } from '../components/studio/effects/GuitarPresets';
import { PresetAnalytics } from '../components/studio/effects/PresetAnalytics';
import { CommunityPresetsBrowser } from '../components/studio/effects/CommunityPresetsBrowser';
import { PresetBundlesBrowser } from '../components/studio/effects/PresetBundlesBrowser';
import { BundleManager } from '../components/studio/effects/BundleManager';
import { AudioTestPanel } from '../components/studio/effects/AudioTestPanel';
import {
  Home,
  FolderOpen,
  Music,
  Sliders,
  BarChart3,
  TrendingUp,
  DollarSign,
  Play,
  Pause,
  Volume2,
  Plus,
  Search,
  Grid,
  List,
  Clock,
  Star,
  MoreVertical,
  ChevronRight,
  Folder,
  FileAudio,
  Download,
  Eye,
  ShoppingCart,
  Upload,
  Settings,
  User,
  Mic,
  Guitar,
  Piano,
  Sparkles,
  Zap,
  Users,
  Package,
  Activity,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tab navigation items
const TABS = [
  { id: 'projects', name: 'Projects', icon: FolderOpen },
  { id: 'mixer', name: 'Quick Mix', icon: Sliders },
  { id: 'community', name: 'Community', icon: Users },
  { id: 'bundles', name: 'Bundles', icon: Package },
  { id: 'analytics', name: 'Analytics', icon: BarChart3 },
];

// Project Card Component
const ProjectCard = ({ project, onOpen, onMore }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
    onClick={() => onOpen(project)}
  >
    <div className="flex items-start gap-3">
      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${
        project.type === 'song' ? 'from-violet-500 to-purple-600' :
        project.type === 'beat' ? 'from-orange-500 to-red-500' :
        'from-cyan-500 to-blue-500'
      } flex items-center justify-center flex-shrink-0`}>
        <Music className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-white truncate">{project.name}</h3>
        <p className="text-sm text-zinc-400 truncate">{project.tracks?.length || 0} tracks • {project.bpm || 120} BPM</p>
        <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500">
          <Clock className="w-3 h-3" />
          <span>{new Date(project.updated_at || project.created_at).toLocaleDateString()}</span>
          {project.folder && (
            <>
              <span>•</span>
              <Folder className="w-3 h-3" />
              <span>{project.folder}</span>
            </>
          )}
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onMore(project); }}
        className="text-zinc-500 hover:text-white p-1"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
    </div>
  </motion.div>
);

// Folder Card Component
const FolderCard = ({ folder, count, onOpen }) => (
  <motion.button
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    onClick={() => onOpen(folder)}
    className="flex items-center gap-3 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-zinc-700 transition-colors w-full text-left"
  >
    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
      <FolderOpen className="w-5 h-5 text-amber-400" />
    </div>
    <div className="flex-1">
      <h4 className="font-medium text-white">{folder}</h4>
      <p className="text-sm text-zinc-500">{count} projects</p>
    </div>
    <ChevronRight className="w-4 h-4 text-zinc-500" />
  </motion.button>
);

// Quick Preset Selector for Mobile
const QuickPresetSelector = ({ selectedPresetId, onSelectPreset }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedPreset = FACTORY_PRESETS.find(p => p.id === selectedPresetId);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2 py-1 rounded bg-zinc-800 border border-zinc-700 hover:border-zinc-600"
      >
        <Zap className="w-3 h-3 text-amber-400" />
        <span className="text-[10px] text-zinc-300 max-w-[60px] truncate">
          {selectedPreset?.name || 'Preset'}
        </span>
      </button>
      
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-full left-0 mb-1 w-48 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 p-2 max-h-48 overflow-y-auto"
        >
          <div className="grid grid-cols-2 gap-1">
            {FACTORY_PRESETS.map(preset => (
              <button
                key={preset.id}
                onClick={() => {
                  onSelectPreset(preset);
                  setIsOpen(false);
                }}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-left transition-colors ${
                  selectedPresetId === preset.id
                    ? 'bg-violet-500/30 border border-violet-500/50'
                    : 'bg-zinc-800 border border-transparent hover:border-zinc-600'
                }`}
              >
                <span className="text-xs">{preset.icon}</span>
                <span className="text-[10px] text-zinc-300 truncate">{preset.name}</span>
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Quick Mix Track Strip
const QuickMixTrack = ({ track, onVolumeChange, onMuteToggle, onSoloToggle, onGuitarEffectsToggle, onPresetSelect }) => {
  const [showPresets, setShowPresets] = useState(false);
  
  const handlePresetSelect = (preset) => {
    const settings = applyPresetToTrack(preset);
    if (settings && onPresetSelect) {
      onPresetSelect(track.id, { ...settings, presetId: preset.id });
    }
    setShowPresets(false);
  };
  
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 p-3">
        <div className={`w-8 h-8 rounded flex items-center justify-center ${
          track.type === 'audio' ? 'bg-cyan-500/20' :
          track.type === 'instrument' ? 'bg-violet-500/20' :
          'bg-emerald-500/20'
        }`}>
          {track.type === 'audio' ? <Mic className="w-4 h-4 text-cyan-400" /> :
           track.type === 'instrument' ? <Piano className="w-4 h-4 text-violet-400" /> :
           <Guitar className="w-4 h-4 text-emerald-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white truncate">{track.name}</p>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="range"
              min="0"
              max="100"
              value={track.volume || 80}
              onChange={(e) => onVolumeChange(track.id, parseInt(e.target.value))}
              className="flex-1 h-1 bg-zinc-700 rounded-full appearance-none cursor-pointer"
            />
            <span className="text-xs text-zinc-400 w-8">{track.volume || 80}%</span>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setShowPresets(!showPresets)}
            className={`w-7 h-7 rounded flex items-center justify-center ${
              track.guitarEffects?.enabled ? 'bg-orange-500/20 text-orange-400' : 'bg-zinc-800 text-zinc-500'
            }`}
            title="Guitar Effects & Presets"
          >
            <Guitar className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onMuteToggle(track.id)}
            className={`w-7 h-7 rounded text-xs font-bold ${
              track.muted ? 'bg-red-500/20 text-red-400' : 'bg-zinc-800 text-zinc-500'
            }`}
          >
            M
          </button>
          <button
            onClick={() => onSoloToggle(track.id)}
            className={`w-7 h-7 rounded text-xs font-bold ${
              track.solo ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800 text-zinc-500'
            }`}
          >
            S
          </button>
        </div>
      </div>
      
      {/* Expandable Preset Section */}
      <AnimatePresence>
        {showPresets && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-zinc-800 overflow-hidden"
          >
            <div className="p-3 bg-zinc-900/30">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs text-zinc-400">Quick Presets</span>
                {track.guitarEffects?.presetId && (
                  <span className="text-[10px] text-violet-400 bg-violet-500/20 px-1.5 py-0.5 rounded">
                    {FACTORY_PRESETS.find(p => p.id === track.guitarEffects?.presetId)?.name}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-4 gap-1">
                {FACTORY_PRESETS.slice(0, 8).map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => handlePresetSelect(preset)}
                    className={`flex flex-col items-center gap-0.5 p-2 rounded transition-colors ${
                      track.guitarEffects?.presetId === preset.id
                        ? 'bg-violet-500/30 border border-violet-500/50'
                        : 'bg-zinc-800/50 border border-transparent hover:border-zinc-600'
                    }`}
                  >
                    <span className="text-sm">{preset.icon}</span>
                    <span className="text-[8px] text-zinc-400 truncate w-full text-center">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Analytics Stat Card
const StatCard = ({ label, value, change, icon: Icon, color }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
    <div className="flex items-center justify-between mb-2">
      <div className={`w-8 h-8 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
        <Icon className={`w-4 h-4 text-${color}-400`} />
      </div>
      {change !== undefined && (
        <span className={`text-xs ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {change >= 0 ? '+' : ''}{change}%
        </span>
      )}
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    <div className="text-sm text-zinc-400">{label}</div>
  </div>
);

// Main Dashboard Component
export default function MobileDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  
  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/studio/projects?user_id=${user.id}`);
        if (response.ok) {
          const data = await response.json();
          setProjects(data.projects || []);
          
          // Extract unique folders
          const uniqueFolders = [...new Set(
            data.projects
              .filter(p => p.folder)
              .map(p => p.folder)
          )];
          setFolders(uniqueFolders);
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, [user]);
  
  // Fetch analytics
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!user || activeTab !== 'analytics') return;
      try {
        // Fetch earnings data
        const earningsRes = await fetch(`${API_URL}/api/payments/earnings?user_id=${user.id}`);
        const earningsData = earningsRes.ok ? await earningsRes.json() : {};
        
        // Mock analytics data (in production, fetch from dedicated endpoint)
        setAnalytics({
          totalEarnings: earningsData.total_earnings || 0,
          totalSales: earningsData.total_sales || 0,
          totalDownloads: earningsData.total_downloads || 0,
          totalPlays: Math.floor(Math.random() * 500) + 100,
          weeklyChange: Math.floor(Math.random() * 30) - 10,
          recentSales: earningsData.recent_transactions?.slice(0, 5) || [],
        });
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      }
    };
    
    fetchAnalytics();
  }, [user, activeTab]);
  
  // Filter projects by search and folder
  const filteredProjects = projects.filter(p => {
    const matchesSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFolder = !currentFolder || p.folder === currentFolder;
    return matchesSearch && matchesFolder;
  });
  
  // Open project in studio
  const handleOpenProject = (project) => {
    navigate(`/studio?project=${project.id}`);
  };
  
  // Create new project
  const handleNewProject = () => {
    navigate('/studio');
  };
  
  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Music className="w-12 h-12 text-violet-400 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">Please log in to access your dashboard</p>
          <Button onClick={() => navigate('/login')} className="bg-violet-600 hover:bg-violet-500">
            Sign In
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white pb-20">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-zinc-900/95 backdrop-blur border-b border-zinc-800">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold">Dashboard</h1>
              <p className="text-sm text-zinc-400">Welcome back, {user.name || 'Producer'}</p>
            </div>
            <div className="flex items-center gap-2">
              <NotificationCenter />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/profile')}
              >
                <User className="w-5 h-5" />
              </Button>
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex gap-2">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-violet-500/20 text-violet-400'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 py-6">
        <AnimatePresence mode="wait">
          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <motion.div
              key="projects"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {/* Search and Actions */}
              <div className="flex gap-3 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search projects..."
                    className="pl-10 bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                <Button
                  onClick={handleNewProject}
                  className="bg-violet-600 hover:bg-violet-500"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New
                </Button>
              </div>
              
              {/* Folder Navigation */}
              {currentFolder ? (
                <div className="flex items-center gap-2 mb-4">
                  <button
                    onClick={() => setCurrentFolder(null)}
                    className="text-sm text-violet-400 hover:text-violet-300"
                  >
                    All Projects
                  </button>
                  <ChevronRight className="w-4 h-4 text-zinc-500" />
                  <span className="text-sm text-white">{currentFolder}</span>
                </div>
              ) : folders.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm text-zinc-400 mb-3">Folders</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {folders.map(folder => (
                      <FolderCard
                        key={folder}
                        folder={folder}
                        count={projects.filter(p => p.folder === folder).length}
                        onOpen={setCurrentFolder}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Projects List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm text-zinc-400">
                    {currentFolder ? `${currentFolder} Projects` : 'Recent Projects'}
                  </h3>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-20 bg-zinc-900/50 rounded-xl animate-pulse" />
                    ))}
                  </div>
                ) : filteredProjects.length === 0 ? (
                  <div className="text-center py-12">
                    <FolderOpen className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500">No projects found</p>
                    <Button onClick={handleNewProject} variant="link" className="text-violet-400 mt-2">
                      Create your first project
                    </Button>
                  </div>
                ) : (
                  <div className={viewMode === 'grid' ? 'grid grid-cols-2 gap-3' : 'space-y-3'}>
                    {filteredProjects.map(project => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        onOpen={handleOpenProject}
                        onMore={(p) => setSelectedProject(p)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
          
          {/* Quick Mix Tab */}
          {activeTab === 'mixer' && (
            <motion.div
              key="mixer"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {selectedProject ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-semibold">{selectedProject.name}</h2>
                      <p className="text-sm text-zinc-400">{selectedProject.tracks?.length || 0} tracks</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon">
                        <Play className="w-5 h-5" />
                      </Button>
                      <Button
                        onClick={() => handleOpenProject(selectedProject)}
                        className="bg-violet-600 hover:bg-violet-500"
                      >
                        Open Studio
                      </Button>
                    </div>
                  </div>
                  
                  {/* Master Volume */}
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-3">
                      <Volume2 className="w-5 h-5 text-violet-400" />
                      <span className="text-sm text-white">Master</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        defaultValue="80"
                        className="flex-1 h-2 bg-zinc-700 rounded-full appearance-none cursor-pointer"
                      />
                      <span className="text-sm text-zinc-400 w-10">80%</span>
                    </div>
                  </div>
                  
                  {/* Track Strips */}
                  <div className="space-y-2">
                    {(selectedProject.tracks || []).map((track, i) => (
                      <QuickMixTrack
                        key={track.id || i}
                        track={track}
                        onVolumeChange={() => {}}
                        onMuteToggle={() => {}}
                        onSoloToggle={() => {}}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Sliders className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-400 mb-2">Select a project to mix</p>
                  <Button variant="link" onClick={() => setActiveTab('projects')} className="text-violet-400">
                    Browse projects
                  </Button>
                </div>
              )}
            </motion.div>
          )}
          
          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <motion.div
              key="analytics"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <StatCard
                  label="Total Earnings"
                  value={`$${(analytics?.totalEarnings || 0).toFixed(2)}`}
                  change={analytics?.weeklyChange}
                  icon={DollarSign}
                  color="emerald"
                />
                <StatCard
                  label="Total Sales"
                  value={analytics?.totalSales || 0}
                  icon={ShoppingCart}
                  color="violet"
                />
                <StatCard
                  label="Downloads"
                  value={analytics?.totalDownloads || 0}
                  icon={Download}
                  color="cyan"
                />
                <StatCard
                  label="Total Plays"
                  value={analytics?.totalPlays || 0}
                  icon={Play}
                  color="amber"
                />
              </div>
              
              {/* Preset Analytics Component */}
              <div className="mb-6">
                <PresetAnalytics userId={user?.id} />
              </div>
              
              {/* Recent Activity */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <h3 className="font-medium text-white mb-4">Recent Sales</h3>
                {analytics?.recentSales?.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.recentSales.map((sale, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                        <div>
                          <p className="text-sm text-white">{sale.preset_name || 'Preset'}</p>
                          <p className="text-xs text-zinc-500">{new Date(sale.created_at).toLocaleDateString()}</p>
                        </div>
                        <span className="text-sm text-emerald-400">+${(sale.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500 text-center py-4">No recent sales</p>
                )}
              </div>
              
              {/* Quick Actions */}
              <div className="mt-6 grid grid-cols-2 gap-4">
                <Button
                  onClick={() => navigate('/marketplace/upload')}
                  className="bg-violet-600 hover:bg-violet-500"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Preset
                </Button>
                <Button
                  onClick={() => navigate('/creator/dashboard')}
                  variant="outline"
                  className="border-zinc-700"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Full Analytics
                </Button>
              </div>
            </motion.div>
          )}
          
          {/* Community Tab */}
          {activeTab === 'community' && (
            <motion.div
              key="community"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <CommunityPresetsBrowser
                userId={user?.id}
                userPresets={[]}
                onDownloadPreset={(preset) => {
                }}
                onPreviewPreset={(preset) => {
                }}
                onRefreshPresets={() => {
                  // Refresh user presets
                }}
              />
            </motion.div>
          )}
          
          {/* Bundles Tab */}
          {activeTab === 'bundles' && (
            <motion.div
              key="bundles"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-8"
            >
              {/* Browse Bundles */}
              <PresetBundlesBrowser
                userId={user?.id}
                onBundlePurchased={(data) => {
                }}
              />
              
              {/* Manage Your Bundles */}
              <div className="border-t border-zinc-800 pt-8">
                <BundleManager userId={user?.id} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur border-t border-zinc-800 px-4 py-2 safe-area-inset-bottom">
        <div className="flex justify-around">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex flex-col items-center gap-1 py-2 px-4 text-violet-400"
          >
            <Home className="w-5 h-5" />
            <span className="text-xs">Home</span>
          </button>
          <button
            onClick={() => navigate('/studio')}
            className="flex flex-col items-center gap-1 py-2 px-4 text-zinc-400 hover:text-white"
          >
            <Music className="w-5 h-5" />
            <span className="text-xs">Studio</span>
          </button>
          <button
            onClick={() => navigate('/marketplace')}
            className="flex flex-col items-center gap-1 py-2 px-4 text-zinc-400 hover:text-white"
          >
            <ShoppingCart className="w-5 h-5" />
            <span className="text-xs">Market</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center gap-1 py-2 px-4 text-zinc-400 hover:text-white"
          >
            <User className="w-5 h-5" />
            <span className="text-xs">Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
}
